// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/planning/ROLE_PERMISSION_MANAGEMENT_PLAN.md
/**
 * Permission Matrix Component
 * 
 * Displays permissions in a matrix format (resources x actions)
 * with checkboxes for granting/revoking permissions.
 */

import React, { useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  Alert,
  Box,
  Tooltip
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

/** Read / View codes are the minimum gate for other actions in the same resource row. */
const isReadOrViewPermission = (permission) => {
  const code = permission?.code ?? '';
  return code.endsWith('_VIEW') || code.endsWith('_READ');
};

const PermissionMatrix = ({
  role,
  permissionData,
  permissionChanges = {},
  onPermissionChange,
  readOnly,
}) => {
  const { hasPermission, isAdmin, user, isLoading } = useAuth();

  /**
   * If API data is inconsistent (non-read granted without read), queue revokes so Save can persist.
   */
  useEffect(() => {
    if (!permissionData?.resources || readOnly) return;
    permissionData.resources.forEach((resource) => {
      const readPerm = resource.permissions.find((p) => isReadOrViewPermission(p));
      if (!readPerm || readPerm.granted) return;
      resource.permissions.forEach((p) => {
        if (p.id === readPerm.id) return;
        if (p.granted) onPermissionChange(p.id, false);
      });
    });
  }, [role?.id, permissionData, readOnly, onPermissionChange]);

  const handleCheckboxChange = (permissionId, granted) => {
    // If Read permission is being unticked, untick all other permissions for the same resource
    if (!granted && isReadPermission(permissionId)) {
      const resource = findResourceByPermissionId(permissionId);
      if (resource) {
        resource.permissions.forEach((permission) => {
          if (permission.id !== permissionId && !isReadPermission(permission.id)) {
            onPermissionChange(permission.id, false);
          }
        });
      }
    }
    onPermissionChange(permissionId, granted);
  };

  const handleSelectAll = (resource) => {
    // Check if all permissions in this resource are currently granted
    const allGranted = resource.permissions.every(p => isPermissionGranted(p));
    
    // Toggle all permissions to opposite of current state
    resource.permissions.forEach(permission => {
      handleCheckboxChange(permission.id, !allGranted);
    });
  };

  const isPermissionGranted = (permission) => {
    if (permissionChanges[permission.id] !== undefined) {
      return permissionChanges[permission.id];
    }
    return permission.granted;
  };

  const isReadPermission = (permissionId) => {
    const permission = findPermissionById(permissionId);
    return isReadOrViewPermission(permission);
  };

  // Helper function to find a permission by ID
  const findPermissionById = (permissionId) => {
    if (!permissionData?.resources) return null;
    
    for (const resource of permissionData.resources) {
      const permission = resource.permissions.find(p => p.id === permissionId);
      if (permission) return permission;
    }
    return null;
  };

  // Helper function to find a resource by permission ID
  const findResourceByPermissionId = (permissionId) => {
    if (!permissionData?.resources) return null;
    
    return permissionData.resources.find(resource => 
      resource.permissions.some(p => p.id === permissionId)
    );
  };

  const isResourceAllGranted = (resource) => {
    return resource.permissions.every(p => isPermissionGranted(p));
  };

  // Map resource categories to their read permissions
  const resourcePermissionMap = {
    chat: 'CHAT_VIEW',
    voice: 'VOICE_VIEW',
    /** Legacy DB only; merged into `voice` by migration z7_voice_retry_merge */
    voice_retry: 'VOICE_VIEW',
    qa: 'QA_VIEW',
    qa_dashboard: 'QA_DASHBOARD_READ',
    models: 'MODEL_READ',
    users: 'USERS_VIEW',
    team_management: 'TEAMS_VIEW',
    teams: 'TEAMS_VIEW',
    chat_prompts: 'CHAT_PROMPTS_READ',
    qa_prompts: 'QA_PROMPTS_READ',
    word_correction: 'WORD_CORRECTION_READ',
    qa_context_settings: 'QA_CONTEXT_SETTINGS_READ',
    translation_glossary: 'TRANSLATION_GLOSSARY_READ',
    voice_processing: 'VOICE_PROCESSING_SETTINGS_READ',
  };

  // Filter resources based on user permissions
  const filteredResources = useMemo(() => {
    if (!permissionData || !permissionData.resources) return [];
    
    // Wait for user data to be loaded before filtering
    if (isLoading || !user) {
      console.log('User data not loaded yet, showing all resources');
      return permissionData.resources;
    }
    
    // Debug logging
    console.log('PermissionMatrix filtering:', {
      roleCode: role?.code,
      isAdminResult: isAdmin(),
      totalResources: permissionData.resources.length,
      teamListResource: permissionData.resources.find(r => r.category === 'team_list'),
      userRole: user?.role,
      userPermissions: user?.permissions?.length || 0
    });
    
    // Admin can see all resources (check by role code)
    if (role?.code === 'ADMIN' || isAdmin()) {
      console.log('Admin access - showing all resources');
      return permissionData.resources;
    }
    
    // Show all resources - don't filter them out
    // The permission matrix should show all resources, but only allow editing
    // of permissions that the current user has access to
    const filtered = permissionData.resources;
    
    const teamListResource = permissionData.resources.find(r => r.category === 'team_list');
    if (teamListResource) {
      console.log('Team List resource found in payload:', teamListResource);
    } else {
      console.log('Team List resource NOT found in payload');
    }
    
    console.log('Filtered resources count:', filtered.length);
    return filtered;
  }, [permissionData, role, isAdmin, hasPermission, user, isLoading]);

  if (!permissionData || !permissionData.resources) {
    console.log('No permission data available:', { permissionData });
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Select a role to view permissions
        </Typography>
      </Paper>
    );
  }
  
  // Debug: Log all resources received
  console.log('PermissionMatrix received resources:', permissionData.resources.map(r => ({
    category: r.category,
    display_name: r.display_name,
    permissions_count: r.permissions?.length || 0
  })));

  if (filteredResources.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="info">
          You don't have permission to view any resources.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ mt: 3 }}>
      {readOnly && (
        <Alert severity="warning" icon={<LockIcon />} sx={{ m: 2 }}>
          <strong>Protected Role:</strong> Administrator role permissions cannot be modified.
        </Alert>
      )}
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#13213c' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '25%' }}>
                Resource
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }}>
                Read
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }}>
                Create
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }}>
                Update
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }}>
                Override
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }}>
                Delete
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredResources.map((resource) => {
              // Debug logging for each resource
              if (resource.category === 'team_management') {
                console.log('Rendering Team Management resource:', resource);
                console.log('Team Management permissions:', resource.permissions);
              }
              
              const permsByAction = {};
              resource.permissions.forEach(p => {
                const action = p.action.toLowerCase();
                if (['read', 'view'].includes(action)) {
                  permsByAction.read = p;
                } else if (['create', 'add'].includes(action)) {
                  permsByAction.create = p;
                } else if (['update', 'edit'].includes(action)) {
                  permsByAction.update = p;
                } else if (action === 'override') {
                  permsByAction.override = p;
                } else if (['delete', 'remove'].includes(action)) {
                  permsByAction.delete = p;
                } else if (action === 'retry') {
                  if (!permsByAction.update) permsByAction.update = p;
                } else if (action === 'manage') {
                  // 'manage' can appear in multiple columns or special handling
                  if (!permsByAction.create) permsByAction.create = p;
                } else if (action === 'filter') {
                  // QA Dashboard: QA_DASHBOARD_FILTER — matrix has no "Filter" column; use Create if free
                  if (!permsByAction.create) permsByAction.create = p;
                } else if (action === 'export') {
                  // QA Dashboard: QA_DASHBOARD_EXPORT — use Update column if free
                  if (!permsByAction.update) permsByAction.update = p;
                }
              });
              
              // Debug logging for QA permissions mapping
              if (resource.category === 'qa') {
                console.log('QA Criteria resource permissions:', resource.permissions);
                console.log('QA Criteria permsByAction:', permsByAction);
                if (permsByAction.override) {
                  console.log('✅ QA_SCORE_OVERRIDE found:', permsByAction.override);
                } else {
                  console.log('❌ QA_SCORE_OVERRIDE NOT found in permsByAction');
                }
              }

              const rowReadGranted =
                !permsByAction.read || isPermissionGranted(permsByAction.read);
              const nonReadDisabled = readOnly || !rowReadGranted;
              const effectiveNonReadChecked = (perm) =>
                rowReadGranted && isPermissionGranted(perm);

              return (
                <TableRow 
                  key={resource.category}
                  sx={{ 
                    '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' },
                    '&:hover': { backgroundColor: '#e9ecef' }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '1.2rem' }}>{resource.icon}</span>
                      <Typography variant="body2" fontWeight="medium">
                        {resource.display_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell align="center">
                    {permsByAction.read ? (
                      <Tooltip title={`${permsByAction.read.name} (${permsByAction.read.code})`}>
                        <Checkbox
                          checked={isPermissionGranted(permsByAction.read)}
                          onChange={(e) => handleCheckboxChange(permsByAction.read.id, e.target.checked)}
                          disabled={readOnly}
                          color="primary"
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">N/A</Typography>
                    )}
                  </TableCell>
                  
                  <TableCell align="center">
                    {permsByAction.create ? (
                      <Tooltip title={`${permsByAction.create.name} (${permsByAction.create.code})`}>
                        <Checkbox
                          checked={effectiveNonReadChecked(permsByAction.create)}
                          onChange={(e) => handleCheckboxChange(permsByAction.create.id, e.target.checked)}
                          disabled={nonReadDisabled}
                          color="primary"
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">N/A</Typography>
                    )}
                  </TableCell>
                  
                  <TableCell align="center">
                    {permsByAction.update ? (
                      <Tooltip title={`${permsByAction.update.name} (${permsByAction.update.code})`}>
                        <Checkbox
                          checked={effectiveNonReadChecked(permsByAction.update)}
                          onChange={(e) => handleCheckboxChange(permsByAction.update.id, e.target.checked)}
                          disabled={nonReadDisabled}
                          color="primary"
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">N/A</Typography>
                    )}
                  </TableCell>
                  
                  <TableCell align="center">
                    {permsByAction.override ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={`${permsByAction.override.name} (${permsByAction.override.code})`}>
                          <Checkbox
                            checked={effectiveNonReadChecked(permsByAction.override)}
                            onChange={(e) => handleCheckboxChange(permsByAction.override.id, e.target.checked)}
                            disabled={nonReadDisabled}
                            color="primary"
                          />
                        </Tooltip>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          {permsByAction.override.code}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled">N/A</Typography>
                    )}
                  </TableCell>
                  
                  <TableCell align="center">
                    {permsByAction.delete ? (
                      <Tooltip title={`${permsByAction.delete.name} (${permsByAction.delete.code})`}>
                        <Checkbox
                          checked={effectiveNonReadChecked(permsByAction.delete)}
                          onChange={(e) => handleCheckboxChange(permsByAction.delete.id, e.target.checked)}
                          disabled={nonReadDisabled}
                          color="primary"
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">N/A</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PermissionMatrix;
